import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, 
  Search, 
  Plus, 
  Upload, 
  Filter, 
  MoreVertical,
  Eye,
  Check,
  X,
  Download,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { contactService } from '../../services/contactService';
import { contactGroupService } from '../../services/contactGroupService';
import toast from 'react-hot-toast';
import ContactModal from './ContactModal';
import ContactGroupList from './ContactGroupList';
import ExcelUploadModal from './ExcelUploadModal';

const ContactManagement = () => {
  const { user } = useAuth();
  const [contactGroups, setContactGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupContacts, setGroupContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [filters, setFilters] = useState({
    status: 'all',
    subscription: 'all'
  });

  // Track if component has mounted to avoid initial search trigger
  const [hasMounted, setHasMounted] = useState(false);

  // Load contact groups on component mount
  useEffect(() => {
    loadContactGroups();
    setHasMounted(true);
  }, [filters]);

  // Handle search term change with debouncing
  useEffect(() => {
    // Skip on initial mount to avoid duplicate API calls
    if (!hasMounted) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      loadContactGroups();
    }, 200); // 200ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]); // Only trigger on search term change

  const loadContactGroups = async () => {
    if (!user?.customerId) return;
    
    setLoading(true);
    try {
      let response;
      
      // Check if search term exists
      if (searchTerm.trim()) {
        // Search for groups containing this phone number
        response = await contactService.searchContactGroupsByPhoneNumber(searchTerm.trim());
        setContactGroups(response?.content || []);
        
        // Show message if no groups found
        if (!response?.content || response.content.length === 0) {
          toast.info(`No contact groups found containing phone number: ${searchTerm.trim()}`);
        }
      } else {
        // Load all contact groups
        response = await contactService.getContactGroups(user.customerId);
        setContactGroups(response || []);
      }
    } catch (error) {
      console.error('Error loading contact groups:', error);
      toast.error('Failed to load contact groups');
    } finally {
      setLoading(false);
    }
  };

  // Handle explicit search button click (optional, search also works on onChange)
  const handleSearch = (e) => {
    if (e) {
      e.preventDefault();
    }
    loadContactGroups();
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactModal(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setShowContactModal(true);
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Are you sure you want to delete contact "${contact.contactName || contact.phoneNumber}"?`)) {
      return;
    }

    try {
      await contactService.deleteContact(contact.contactId);
      toast.success('Contact deleted successfully');
      if (selectedGroup) {
        loadGroupContacts(selectedGroup.groupId);
      }
      loadContactGroups();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const loadGroupContacts = async (groupId) => {
    setLoadingContacts(true);
    try {
      const groupDetails = await contactGroupService.getContactGroupById(groupId);
      setGroupContacts(groupDetails.members || []);
    } catch (error) {
      console.error('Error loading group contacts:', error);
      toast.error('Failed to load group contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleDownloadGroup = async (group) => {
    try {
      const blob = await contactService.downloadGroupContacts(group.groupId, user.customerId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${group.groupName}_contacts.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${group.groupName} contacts`);
    } catch (error) {
      console.error('Error downloading group contacts:', error);
      toast.error('Failed to download group contacts');
    }
  };

  const handleViewGroupDetails = async (group) => {
    setSelectedGroup(group);
    await loadGroupContacts(group.groupId);
  };

  const handleEditGroup = async (group) => {
    const newName = prompt('Enter new group name:', group.groupName);
    if (!newName || newName.trim() === '' || newName === group.groupName) {
      return;
    }

    try {
      await contactGroupService.updateContactGroup(group.groupId, {
        groupName: newName.trim()
      });
      toast.success('Group updated successfully');
      loadContactGroups();
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error('Failed to update group');
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Are you sure you want to delete the group "${group.groupName}"? This will not delete the contacts, only remove them from this group.`)) {
      return;
    }

    try {
      await contactGroupService.deleteContactGroup(group.groupId);
      toast.success('Group deleted successfully');
      loadContactGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const handleCreateGroup = async () => {
    const groupName = prompt('Enter group name:');
    if (!groupName || groupName.trim() === '') {
      return;
    }

    try {
      await contactGroupService.createContactGroup({
        groupName: groupName.trim()
      });
      toast.success('Group created successfully');
      loadContactGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    }
  };

  const handleExcelUploadSuccess = (results) => {
    setShowExcelModal(false);
    setUploadResults(results);
    loadContactGroups(); // Reload groups after successful upload
    
    // Clear the upload results after 10 seconds
    setTimeout(() => {
      setUploadResults(null);
    }, 10000);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-8 pt-8 pb-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Contact Management</h1>
        <p className="text-gray-600">Manage your contact groups and upload contacts from Excel files</p>
      </div>

      {/* Upload Results Message */}
      {uploadResults && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-green-800">Upload Completed Successfully!</h3>
                <div className="flex items-center space-x-6 mt-1">
                  <div className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                    <span className="text-green-700 font-medium">{uploadResults.totalContactsCreated} Created</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <AlertCircle className="h-4 w-4 text-yellow-600 mr-1" />
                    <span className="text-yellow-700 font-medium">{uploadResults.totalContactsAlreadyExists} Already Exists</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <XCircle className="h-4 w-4 text-red-600 mr-1" />
                    <span className="text-red-700 font-medium">{uploadResults.totalContactsWithErrors} Errors</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Total Processed: <span className="font-medium">{uploadResults.totalContactsProcessed}</span>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setUploadResults(null)}
              className="text-green-400 hover:text-green-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Search and Actions */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 max-w-md">
              <div className="relative flex">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Enter phone number to search groups (e.g., 1234567890) or leave empty to see all groups"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field pl-10 pr-10 w-full"
                  />
                </div>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="ml-2 px-3 py-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="ml-2 btn-primary flex items-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Search
                </button>
              </div>
            </form>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleCreateGroup}
                className="btn-secondary flex items-center"
              >
                <Users className="h-4 w-4 mr-2" />
                Create Group
              </button>
              <button
                onClick={() => setShowExcelModal(true)}
                className="btn-secondary flex items-center"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Excel
              </button>
              <button
                onClick={handleAddContact}
                className="btn-primary flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </button>
            </div>
          </div>

          {/* Filters */}
          {/* <div className="mt-4 flex flex-wrap items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">Filter by:</span>
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="input-field text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={filters.subscription}
              onChange={(e) => setFilters(prev => ({ ...prev, subscription: e.target.value }))}
              className="input-field text-sm"
            >
              <option value="all">All Subscriptions</option>
              <option value="subscribed">Subscribed</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
            <button
              onClick={loadContactGroups}
              className="btn-secondary flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div> */}
        </div>
      </div>

      {/* Contact Groups List */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {searchTerm.trim() 
                ? `Groups containing phone number: ${searchTerm} (${contactGroups.length})`
                : `Contact Groups (${contactGroups.length})`
              }
            </h3>
          </div>
        </div>
        <div className="card-body p-0">
          <ContactGroupList
            contactGroups={contactGroups}
            loading={loading}
            onDownloadGroup={handleDownloadGroup}
            onViewGroupDetails={handleViewGroupDetails}
            onEditGroup={handleEditGroup}
            onDeleteGroup={handleDeleteGroup}
          />
        </div>
      </div>

      {/* Modals */}
      {showContactModal && (
        <ContactModal
          customerId={user?.customerId}
          contact={editingContact}
          onClose={() => {
            setShowContactModal(false);
            setEditingContact(null);
          }}
          onSaved={() => {
            setShowContactModal(false);
            setEditingContact(null);
            loadContactGroups();
            if (selectedGroup) {
              loadGroupContacts(selectedGroup.groupId);
            }
          }}
        />
      )}

      {/* Group Details Modal */}
      {selectedGroup && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedGroup(null)} />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {selectedGroup.groupName} - Contacts
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {groupContacts.length} contact{groupContacts.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedGroup(null)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {loadingContacts ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {groupContacts.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No contacts in this group</p>
                    ) : (
                      <div className="space-y-2">
                        {groupContacts.map((contact) => (
                          <div
                            key={contact.contactId}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{contact.contactName || 'Unnamed'}</p>
                              <p className="text-sm text-gray-500">{contact.phoneNumber}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditContact(contact)}
                                className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                title="Edit contact"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteContact(contact)}
                                className="p-2 text-gray-600 hover:text-error-600 hover:bg-error-50 rounded-lg transition-colors"
                                title="Delete contact"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showExcelModal && (
        <ExcelUploadModal
          customerId={user?.customerId}
          onClose={() => setShowExcelModal(false)}
          onSuccess={handleExcelUploadSuccess}
        />
      )}
      </div>
    </div>
  );
};

export default ContactManagement;
